import { useIntl } from 'react-intl';
import { useParams } from 'react-router-dom';
import { Box, Link, paletteColors, Stack, Typography } from '@evaneos/b2b-ui';
import ErrorOutline from '@evaneos/b2b-ui/icons/ErrorOutline';

import { ERROR, PENDING } from '../../../../design-system/constants';
import { CircularLoader } from '../../../../design-system/Loader/CircularLoader';
import { sendMessageRequest } from '../../../../resources/discussion/actions';
import {
    getContentStatusBySendingMessageId,
    getSendingMessageById,
} from '../../../../resources/discussion/selectors';
import { AttachmentsById } from '../../../../resources/discussion/types';
import { DossierId } from '../../../../resources/dossier/types';
import { useAppDispatch, useAppSelector } from '../../../../shared/redux';
import { messageHtmlConverter } from '../utils/messageHtmlConverter';

import s from './Discussion.css';
import { MessageAttachment } from './MessageAttachment';

type Props = {
    sendingMessageId: string;
    attachments: AttachmentsById;
};

export const SendingMessage = ({ sendingMessageId, attachments }: Props): JSX.Element | null => {
    const { formatMessage } = useIntl();
    const { dossierId } = useParams<{ dossierId: DossierId }>();
    const dispatch = useAppDispatch();

    const sendingMessage = useAppSelector(getSendingMessageById(dossierId, sendingMessageId));
    const status = useAppSelector(getContentStatusBySendingMessageId(dossierId, sendingMessageId));

    if (!sendingMessage) {
        return null;
    }

    const attachmentsList = Object.values(attachments);

    const retry = () => {
        const attachmentIds = attachmentsList.map((att) => att.id).filter((id) => id !== undefined);

        dispatch(
            sendMessageRequest(
                dossierId,
                sendingMessage.id,
                sendingMessage.content,
                sendingMessage.contentHtml,
                attachmentIds,
            ),
        );
    };

    return (
        <Stack
            flex={1}
            justifyContent="end"
            width="100%"
            data-testid="sending-message"
            marginTop={3}
        >
            <Stack flexDirection="column" alignItems="flex-end">
                <Box
                    bgcolor={status === ERROR ? paletteColors.red[100] : paletteColors.green[100]}
                    className={s.message}
                    sx={{
                        position: 'relative',
                        maxWidth: '720px',
                        borderRadius: '16px',
                        whiteSpace: 'pre-line',
                        wordBreak: 'break-word',
                        width: 'fit-content',
                        transition: 'background-color 0.2s ease-in-out',
                    }}
                    p={2}
                >
                    <Typography
                        variant="body2"
                        sx={{
                            '& *': {
                                fontSize: '1.4rem',
                                lineHeight: '2rem',
                                fontFamily: 'Open Sauce One, sans-serif',
                            },
                        }}
                        dangerouslySetInnerHTML={{
                            __html: messageHtmlConverter(
                                sendingMessage.content,
                                sendingMessage.contentHtml,
                            ),
                        }}
                    />
                    <Box display="flex" flexWrap="wrap">
                        {attachmentsList.map(({ file, filenameId }) => {
                            const type = file.name.replace(/.*\./, '');
                            return (
                                <MessageAttachment key={filenameId} name={file.name} type={type} />
                            );
                        })}
                    </Box>
                </Box>
                {status === PENDING && (
                    <Box display="flex" alignItems="center" justifyContent="flex-end" gap={0.5}>
                        <Typography variant="caption" color="textSecondary" fontWeight="bold">
                            {formatMessage({ id: 'discussion.sending' })}
                        </Typography>
                        <CircularLoader
                            color={paletteColors.neutral[700]}
                            style={{ width: '16px' }}
                            data-testid="sending-message-loader"
                        />
                    </Box>
                )}
                {status === ERROR && (
                    <Stack flexDirection="row" alignItems="center" gap={0.5}>
                        <Typography
                            variant="caption"
                            color={paletteColors.red[900]}
                            fontWeight="bold"
                            component="span"
                        >
                            {formatMessage({ id: 'discussion.not_sent' })}
                            {' • '}
                            <Link
                                color="inherit"
                                sx={{
                                    fontWeight: 'bold',
                                    '&:hover': { color: paletteColors.red[800] },
                                }}
                                onClick={retry}
                                data-testid="sending-message-send-retry"
                            >
                                {formatMessage({ id: 'global.retry' })}
                            </Link>
                        </Typography>
                        <ErrorOutline color="error" fontSize="small" />
                    </Stack>
                )}
            </Stack>
        </Stack>
    );
};
